import { v4 } from 'uuid';
import type { Interaction, InteractionColors, InteractionSettings } from '@components/Presentation/types';

export interface PatchData {
  rosTitle?: string;
  layoutMode?: string;
  slides?: Interaction[];
  deletedSlideIds?: string[];
  slidePatches?: Record<
    string,
    Partial<Omit<Interaction, 'color' | 'setting' | 'options'>> & {
      color?: Partial<InteractionColors>;
      setting?: Partial<InteractionSettings>;
      options?: Interaction['options'];
    }
  >;
}

export interface PresentationPatch {
  id: string;
  ts: number;
  data: PatchData;
}

export function createPatch(data: PatchData): PresentationPatch {
  return { id: v4(), ts: Date.now(), data };
}

/**
 * Merges an ordered list of patches (oldest → newest) into a single PatchData.
 * Rule: latest value wins per field. For nested color/setting, latest key wins.
 * A structural `slides` patch (add/delete/reorder) resets the accumulated
 * slidePatches accumulator because the new slides array already bakes them in.
 */
export function mergePatches(patches: PresentationPatch[]): PatchData {
  const result: PatchData = {};
  const pendingSlidePatches: NonNullable<PatchData['slidePatches']> = {};

  for (const { data } of patches) {
    if (data.rosTitle !== undefined) result.rosTitle = data.rosTitle;
    if (data.layoutMode !== undefined) result.layoutMode = data.layoutMode;
    if (data.deletedSlideIds?.length) {
      result.deletedSlideIds = [
        ...(result.deletedSlideIds ?? []),
        ...data.deletedSlideIds,
      ];
    }

    if (data.slides !== undefined) {
      result.slides = data.slides;
      for (const k of Object.keys(pendingSlidePatches)) delete pendingSlidePatches[k];
    }

    if (data.slidePatches) {
      for (const [id, patch] of Object.entries(data.slidePatches)) {
        const prev = pendingSlidePatches[id] ?? {};
        pendingSlidePatches[id] = {
          ...prev,
          ...patch,
          ...(patch.color ? { color: { ...prev.color, ...patch.color } } : {}),
          ...(patch.setting ? { setting: { ...prev.setting, ...patch.setting } } : {}),
          ...(patch.options !== undefined ? { options: patch.options } : {}),
        };
      }
    }
  }

  if (Object.keys(pendingSlidePatches).length > 0) {
    result.slidePatches = pendingSlidePatches;
  }
  return result;
}

/**
 * Applies merged patches to a base slides array.
 * Structural slides (from `merged.slides`) take priority; per-slide patches
 * are applied on top of the structural base.
 */
export function applyPatches(currentSlides: Interaction[], merged: PatchData): Interaction[] {
  const base = merged.slides ?? currentSlides;
  if (!merged.slidePatches) return base;

  return base.map((slide) => {
    if (!slide.id) return slide;
    const patch = merged.slidePatches![slide.id];
    if (!patch) return slide;
    return {
      ...slide,
      ...patch,
      color: patch.color ? { ...slide.color, ...patch.color } : slide.color,
      setting: patch.setting ? { ...slide.setting, ...patch.setting } : slide.setting,
    };
  });
}
